import React from "react";
import { Box, Typography, Stack, Button } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useCurrentBrand } from "../../../hooks/useCurrentWorkspace";
import GetItOnGooglePlay from "../../../icons/GetItOnGooglePlay";
import DownloadOnAppStore from "../../../icons/DownloadOnAppStore";
import { Link, Check } from "lucide-react";

const useStyles = makeStyles((theme) => ({
  header: {
    marginBottom: theme.spacing(2.5),
  },
  titleBox: {
    display: "flex",
    alignItems: "center",
    gap: 0,
    flexDirection: "column",
    marginBottom: theme.spacing(2),

    [theme.breakpoints.up("sm")]: {
      flexDirection: "row",
      gap: theme.spacing(2),
      marginBottom: 0,
    },
  },
  title: {
    fontSize: 32,
    fontWeight: "bold",
    color: theme.palette.common.black,
    marginBottom: theme.spacing(1),
  },
  statusBox: {
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    padding: theme.spacing(0.5, 2),
    flexShrink: 0,
    marginBottom: 4,
  },
  status: {
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
  },
  text: {
    fontSize: 16,
    fontWeight: 500,
    color: theme.palette.common.black,
  },
  links: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(1),
    alignItems: "center",
    flexDirection: "column",
  },
}));

const CopyLinkButton = ({ link }: { link: string }) => {
  const [copied, setCopied] = React.useState(false);

  const copyLink = (link: string) => {
    return () => {
      navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200); // show checkmark for 1.2s
    };
  };

  return (
    <Button
      variant="outlined"
      color="primary"
      fullWidth
      sx={{
        display: "flex",
        gap: 1,
      }}
      onClick={copyLink(link)}
    >
      {copied ? "Copied!" : "Copy link"}
      <Box
        sx={{
          display: "inline-flex",
          position: "relative",
          width: 20,
          height: 20,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            opacity: copied ? 0 : 1,
            transform: copied
              ? "scale(0.5) rotate(-20deg)"
              : "scale(1) rotate(0deg)",
            transition:
              "opacity 0.2s, transform 0.3s cubic-bezier(0.4,0,0.2,1)",
          }}
        >
          <Link size={20} />
        </Box>
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            opacity: copied ? 1 : 0,
            transform: copied ? "scale(1.2)" : "scale(0.5)",
            transition:
              "opacity 0.2s, transform 0.3s cubic-bezier(0.4,0,0.2,1)",
          }}
        >
          <Check size={20} />
        </Box>
      </Box>
    </Button>
  );
};

export function PublishedAppContent() {
  const s = useStyles();
  const { brandName, appName, brandedAppLogo, AppleStoreUrl, GoogleStoreUrl } =
    useCurrentBrand();

  return (
    <Box className={s.header}>
      <Box className={s.titleBox}>
        <Typography className={s.title}>{brandName} Branded App</Typography>
        <Box className={s.statusBox}>
          <Typography className={s.status}>published</Typography>
        </Box>
      </Box>
      <Stack
        spacing={2}
        sx={{
          marginTop: 2,
        }}
      >
        <Typography className={s.text}>
          Your custom branded app is officially live🔥 on both Google Play
          Market and the Apple App Store! This means your clients can easily
          install and start using your app, creating a seamless and professional
          experience with your brand from the very first interaction.
        </Typography>
        <Typography className={s.text}>
          Direct links to your app are below. Your clients also have these links
          within their profiles for quick access. Now, your brand is always
          within reach, helping you stay front and center in your clients'
          fitness journeys!
        </Typography>
        <Box className={s.links}>
          <Box
            component={"img"}
            sx={{
              width: 100,
              height: 100,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginTop: 2,
              borderRadius: "20%",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
            }}
            src={brandedAppLogo}
          ></Box>
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 600,
              letterSpacing: 0.5,
              color: "#333",
              textTransform: "capitalize",
              marginTop: 1,
            }}
          >
            {appName}
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              justifyContent: "center",
              my: 2,
              maxWidth: 350,
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <GetItOnGooglePlay href={GoogleStoreUrl} />
              <CopyLinkButton link={GoogleStoreUrl} />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <DownloadOnAppStore href={AppleStoreUrl} />
              <CopyLinkButton link={AppleStoreUrl} />
            </Box>
          </Box>
        </Box>
        <Typography className={s.text}>
          No effort from you is required to migrate your existing and future
          clients to your brand new mobile app from Stridist.
          <br /> - We're promoting it to your existing clients, ensuring they
          continue their fitness journey with your brand only
          <br /> - Every future client of yours will not know what is Stridist -
          only <b>{appName}</b> from now on.
        </Typography>
      </Stack>
    </Box>
  );
}
